import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { ArrowLeft, ArrowRight, Lock, Maximize2, Printer, BookOpen, Presentation } from 'lucide-react';

import * as assets from '../../../assets';
import { theme, GlobalStyle, Viewport, Stage, Chrome, ProgressBar, Gate } from './styles';
import { ResponsesProvider, useResponses, SaveStatus } from './Capture';

/* ======================================================
   EngageDeck
   A password-gated, 16:9 clickable presentation that uses
   the optiii.com theme. Each client engagement supplies its
   own slides + password from engage/[company]/[code]/.
====================================================== */

const SLIDE_W = 1920;
const SLIDE_H = 1080;

export const Logo = ({ size = 30 }) => (
  <img
    src={assets.LogoOptiiiIconOnly}
    alt="Optiii"
    // The icon asset is navy; render it light so it reads on the dark theme
    style={{ width: size, height: size, filter: 'brightness(0) invert(0.96)' }}
  />
);

export const Brand = ({ size }) => (
  <div className="brand">
    <Logo size={size} />
    Optiii
  </div>
);

const useNoIndex = (title) => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.title = prevTitle;
      meta.remove();
    };
  }, [title]);
};

/* ---------- Answered counter (in the control bar) ---------- */
const AnsweredCount = () => {
  const { answers, questions } = useResponses();
  if (!questions.length) return null;
  const n = questions.filter((q) => (answers[q.id] || []).length).length;
  return (
    <span className="count answered" title="Questions answered so far">
      {n}/{questions.length} answered · <SaveStatus />
    </span>
  );
};

/* ---------- Password gate ---------- */

const ClientMark = ({ src, name, size = 20 }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return null;
  return <img className="clientLogo" src={src} alt={name} style={{ width: size, height: size }} onError={() => setFailed(true)} />;
};

const PasswordGate = ({ preparedFor, clientLogo, onUnlock, check }) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');

  // Checked on click / Enter rather than a form submit, so it also works in
  // sandboxed previews that block form submission.
  const submit = (e) => {
    if (e) e.preventDefault();
    if (check(value.trim())) onUnlock();
    else setError('That password didn’t work. Please try again.');
  };

  return (
    <Gate role="dialog" aria-label="Private presentation">
      <div className="heroBg" />
      <div className="wrap">
        <div className="gateBrand">
          <Logo size={18} />
          Optiii
        </div>
        <div className="formCard">
          <div className="gateEyebrow">
            <Lock size={14} />
            Private presentation
          </div>
          <h1 className="gateTitle">
            <ClientMark src={clientLogo} name={preparedFor} size={28} />
            Prepared for {preparedFor}
          </h1>
          <p>Enter the password you were given to view this presentation.</p>
          <label htmlFor="engage-pw">Password</label>
          <input
            id="engage-pw"
            type="password"
            placeholder="Password"
            autoFocus
            required
            value={value}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit(e);
            }}
            onChange={(e) => {
              setValue(e.target.value);
              setError('');
            }}
          />
          <button type="button" onClick={submit}>
            View presentation <ArrowRight size={18} />
          </button>
          <div className="err" role="alert">{error}</div>
        </div>
        <div className="foot">Confidential · © Optiii · team@optiii.com</div>
      </div>
    </Gate>
  );
};

/* ---------- Deck ---------- */

const EngageDeck = ({ title, preparedFor, clientLogo, password, storageKey, slides, questions = [], saveUrl }) => {
  useNoIndex(title);

  const [unlocked, setUnlocked] = useState(() => {
    try {
      return sessionStorage.getItem(storageKey) === '1';
    } catch (e) {
      return false;
    }
  });

  const total = slides.length;
  const initial = (() => {
    const n = parseInt(window.location.hash.slice(1), 10);
    return Number.isFinite(n) ? Math.min(Math.max(n - 1, 0), total - 1) : 0;
  })();

  const [index, setIndex] = useState(initial);
  const [scale, setScale] = useState(1);
  const touchX = useRef(null);

  // Meeting mode: arrows skip slides tagged as post-reading
  const [meetingMode, setMeetingMode] = useState(() => {
    try {
      return sessionStorage.getItem(`${storageKey}-meeting`) === '1';
    } catch (e) {
      return false;
    }
  });
  useEffect(() => {
    try {
      sessionStorage.setItem(`${storageKey}-meeting`, meetingMode ? '1' : '0');
    } catch (e) {}
  }, [meetingMode, storageKey]);

  const go = useCallback(
    (n) => setIndex(Math.max(0, Math.min(total - 1, n))),
    [total]
  );

  const step = useCallback(
    (dir) => {
      let n = index + dir;
      if (meetingMode) {
        while (n >= 0 && n < total && slides[n].postRead) n += dir;
        if (n < 0 || n >= total) return; // nothing left to show in meeting mode
      }
      go(n);
    },
    [index, meetingMode, total, slides, go]
  );

  // Keep page from scrolling while the deck is mounted
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Fit the 1920×1080 stage to the window
  useEffect(() => {
    const fit = () => setScale(Math.min(window.innerWidth / SLIDE_W, window.innerHeight / SLIDE_H));
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  // Deep link to a slide (#3)
  useEffect(() => {
    try {
      window.history.replaceState(null, '', `#${index + 1}`);
    } catch (e) {}
  }, [index]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  // Keyboard navigation
  useEffect(() => {
    if (!unlocked) return undefined;
    const onKey = (e) => {
      // Don't hijack keys while someone is typing or using a control
      const tag = (e.target.tagName || '').toLowerCase();
      if (['input', 'textarea', 'select', 'button'].includes(tag) || e.target.getAttribute?.('role') === 'button') return;
      if (['ArrowRight', 'PageDown', ' ', 'Enter'].includes(e.key)) {
        e.preventDefault();
        step(1);
      } else if (['ArrowLeft', 'PageUp', 'Backspace'].includes(e.key)) {
        e.preventDefault();
        step(-1);
      } else if (e.key === 'Home') go(0);
      else if (e.key === 'End') go(total - 1);
      else if (e.key === 'f') toggleFullscreen();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [unlocked, index, total, go, step]);

  const unlock = () => {
    try {
      sessionStorage.setItem(storageKey, '1');
    } catch (e) {}
    setUnlocked(true);
  };

  const pageLabel = (n) => `${String(n + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;

  return (
    <ThemeProvider theme={theme}>
      <ResponsesProvider storageKey={storageKey} deck={storageKey} questions={questions} saveUrl={saveUrl}>
      <GlobalStyle />

      {!unlocked ? (
        <PasswordGate preparedFor={preparedFor} clientLogo={clientLogo} onUnlock={unlock} check={(v) => v === password} />
      ) : (
        <>
          <ProgressBar style={{ width: `${((index + 1) / total) * 100}%` }} />

          <Viewport
            onTouchStart={(e) => {
              touchX.current = e.touches[0].clientX;
            }}
            onTouchEnd={(e) => {
              if (touchX.current === null) return;
              const d = e.changedTouches[0].clientX - touchX.current;
              if (Math.abs(d) > 40) step(d < 0 ? 1 : -1);
              touchX.current = null;
            }}
          >
            <Stage style={{ transform: `scale(${scale})` }}>
              {slides.map((s, n) => (
                <section
                  key={s.id}
                  className={`slide ${s.variant || ''} ${n === index ? 'active' : ''}`}
                  style={s.style}
                  aria-hidden={n !== index}
                >
                  {s.hero && <div className="heroBg" />}
                  {s.postRead && (
                    <span className="postTag">
                      <BookOpen size={15} /> Appendix · for reading after the meeting
                    </span>
                  )}
                  {/* Content is drawn at full size, then scaled down for breathing room */}
                  <div className={`slideBody ${s.variant === 'center' ? 'center' : ''}`}>{s.content}</div>
                  {s.footnote && <div className="footnote">{s.footnote}</div>}
                  <div className="footer">
                    <span className="footBrand">
                      <Brand size={18} />
                      <span className="footSection">{s.section || `Prepared for ${preparedFor} · Confidential`}</span>
                    </span>
                    <span className="footRight">
                      <span className="footClient">
                        <ClientMark src={clientLogo} name={preparedFor} size={18} />
                        {preparedFor}
                      </span>
                      <span>{n === 0 ? 'Use ← → to navigate' : pageLabel(n)}</span>
                    </span>
                  </div>
                </section>
              ))}
            </Stage>
          </Viewport>

          <Chrome>
            <button onClick={() => step(-1)} aria-label="Previous slide" title="Previous (←)">
              <ArrowLeft size={13} />
            </button>
            <span className="count">
              {index + 1}/{total}
            </span>
            <button onClick={() => step(1)} aria-label="Next slide" title="Next (→)">
              <ArrowRight size={13} />
            </button>
            <span className="sep" />
            <AnsweredCount />
            <span className="sep" />
            <button
              onClick={() => setMeetingMode((m) => !m)}
              className={meetingMode ? 'on' : ''}
              aria-pressed={meetingMode}
              title="Meeting mode: skip slides marked for reading after the meeting"
            >
              <Presentation size={13} /> Meeting
            </button>
            <button onClick={toggleFullscreen} aria-label="Full screen" title="Full screen (F)">
              <Maximize2 size={13} />
            </button>
            <button onClick={() => window.print()} aria-label="Print or save as PDF" title="Print / PDF">
              <Printer size={13} />
            </button>
          </Chrome>
        </>
      )}
      </ResponsesProvider>
    </ThemeProvider>
  );
};

export default EngageDeck;
